/**
 * Centralized social links configuration for bntvllnt
 * Single source of truth for all social URLs across the app
 */

// =============================================================================
// Social Profile URLs
// =============================================================================

export const SOCIAL_URLS = {
  discord: "https://discord.gg/vllnt",
  github: "https://github.com/bntvllnt",
  linkedin: "https://linkedin.com/in/bntvllnt",
  x: "https://x.com/bntvllnt",
} as const;

// =============================================================================
// Social Link Types
// =============================================================================

export type SocialLink = {
  href: string;
  label: string;
};

// =============================================================================
// Pre-configured Social Links Arrays
// =============================================================================

/**
 * Primary social links for Hero components and general use
 * X + GitHub as the main public profiles
 */
export const PRIMARY_SOCIAL_LINKS: SocialLink[] = [
  { href: SOCIAL_URLS.x, label: "X (Twitter)" },
  { href: SOCIAL_URLS.linkedin, label: "LinkedIn" },
  { href: SOCIAL_URLS.github, label: "GitHub" },
];

/**
 * Full social links including LinkedIn
 * For comprehensive contact/about pages
 */
export const ALL_SOCIAL_LINKS: SocialLink[] = [
  { href: SOCIAL_URLS.x, label: "X (Twitter)" },
  { href: SOCIAL_URLS.github, label: "GitHub" },
  { href: SOCIAL_URLS.linkedin, label: "LinkedIn" },
];

/**
 * Social profiles for structured data (schema.org sameAs)
 */
export const SCHEMA_SAME_AS: readonly string[] = [
  SOCIAL_URLS.x,
  SOCIAL_URLS.github,
  SOCIAL_URLS.linkedin,
] as const;

// =============================================================================
// UTM Parameters
// =============================================================================

function addUtmParameters(url: string, source: string): string {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}utm_source=${source}&utm_medium=social&utm_campaign=share`;
}

// =============================================================================
// Share Intent URL Builders
// =============================================================================

/**
 * Build X/Twitter share intent URL
 */
export function buildXShareUrl(url: string, text: string): string {
  const urlWithUtm = addUtmParameters(url, "x");
  return `https://twitter.com/intent/tweet?url=${encodeURIComponent(urlWithUtm)}&text=${encodeURIComponent(text)}`;
}

/**
 * Build LinkedIn share URL
 */
export function buildLinkedInShareUrl(url: string): string {
  const urlWithUtm = addUtmParameters(url, "linkedin");
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(urlWithUtm)}`;
}

/**
 * Build Facebook share URL
 */
export function buildFacebookShareUrl(url: string): string {
  const urlWithUtm = addUtmParameters(url, "facebook");
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(urlWithUtm)}`;
}

/**
 * Build Bluesky share URL
 */
export function buildBlueskyShareUrl(url: string, text: string): string {
  const urlWithUtm = addUtmParameters(url, "bluesky");
  return `https://bsky.app/intent/compose?text=${encodeURIComponent(`${text} ${urlWithUtm}`)}`;
}

/**
 * Build URL with UTM for copy to clipboard
 */
export function buildCopyUrl(url: string): string {
  return addUtmParameters(url, "copy");
}
